import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Printer } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ComboV2PlanPrint() {
  const { planId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tk = localStorage.getItem('session_token');
        const r = await axios.get(`${API}/api/combo-v2/plans/${planId}/print`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load plan');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [planId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading plan…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-rose-600">
        {error || 'Plan not found'}
      </div>
    );
  }

  const fmt = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  return (
    <div className="bg-white text-stone-900 min-h-screen print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
      `}</style>

      {/* Toolbar — hidden on print */}
      <div className="no-print sticky top-0 z-10 bg-stone-100 border-b border-stone-200 px-6 py-3 flex justify-between items-center">
        <div className="text-sm text-stone-600">
          Bulk Assembly Plan · <span className="font-mono">{data.plan_id}</span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => window.print()}
            data-testid="print-now-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-[210mm] mx-auto px-8 py-6 print:px-0 print:py-0" data-testid="plan-print-page">
        {/* Header */}
        <div className="border-b-2 border-stone-900 pb-3 mb-5">
          <h1 className="text-2xl font-bold uppercase tracking-wide">Bulk Assembly Plan</h1>
          <div className="flex justify-between items-end mt-1">
            <div>
              <div className="text-lg font-semibold" data-testid="plan-name">
                {data.plan_name || '—'}
              </div>
              <div className="text-xs text-stone-500 font-mono">{data.plan_id}</div>
            </div>
            <div className="text-right text-xs text-stone-600">
              <div>Status: <strong>{data.status || '—'}</strong></div>
              <div>Created: {fmt(data.created_at)}</div>
              <div>By: {data.created_by || '—'}</div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-5 text-center">
          <div className="border border-stone-300 rounded p-2">
            <div className="text-xs uppercase tracking-wide text-stone-500">Sessions</div>
            <div className="text-2xl font-bold" data-testid="plan-print-sessions-count">{data.total_sessions}</div>
          </div>
          <div className="border border-stone-300 rounded p-2">
            <div className="text-xs uppercase tracking-wide text-stone-500">Pick Slips</div>
            <div className="text-2xl font-bold" data-testid="plan-print-slips-count">{data.total_slips}</div>
          </div>
          <div className="border border-stone-300 rounded p-2">
            <div className="text-xs uppercase tracking-wide text-stone-500">Total Combos Targeted</div>
            <div className="text-2xl font-bold">{data.total_qty}</div>
          </div>
        </div>

        {/* Sessions */}
        <h2 className="text-base font-semibold border-b border-stone-300 pb-1 mb-2">Combo Assembly Sessions</h2>
        <table className="w-full text-xs mb-6 border border-stone-300" data-testid="plan-print-sessions-table">
          <thead className="bg-stone-100">
            <tr>
              <th className="text-left p-1.5 border-r border-stone-300 w-8">#</th>
              <th className="text-left p-1.5 border-r border-stone-300">Session ID</th>
              <th className="text-left p-1.5 border-r border-stone-300">Design Name</th>
              <th className="text-left p-1.5 border-r border-stone-300">Description</th>
              <th className="text-left p-1.5 border-r border-stone-300">SKU</th>
              <th className="text-left p-1.5 border-r border-stone-300">Shade · Size</th>
              <th className="text-right p-1.5 border-r border-stone-300">Target</th>
              <th className="text-right p-1.5">Assembled</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.length === 0 ? (
              <tr><td className="p-3 text-center text-stone-500" colSpan={8}>No sessions</td></tr>
            ) : data.sessions.map((s, i) => (
              <tr key={s.session_id} className="border-t border-stone-200">
                <td className="p-1.5 border-r border-stone-200">{i + 1}</td>
                <td className="p-1.5 border-r border-stone-200 font-mono">{s.session_id}</td>
                <td className="p-1.5 border-r border-stone-200">{s.design_name || '—'}</td>
                <td className="p-1.5 border-r border-stone-200">{s.description || '—'}</td>
                <td className="p-1.5 border-r border-stone-200 font-mono">{s.combo_barcode}</td>
                <td className="p-1.5 border-r border-stone-200">{[s.shade, s.size].filter(Boolean).join(' · ') || '—'}</td>
                <td className="p-1.5 border-r border-stone-200 text-right">{s.target_quantity}</td>
                <td className="p-1.5 text-right">{s.assembled_quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pick Slips */}
        <h2 className="text-base font-semibold border-b border-stone-300 pb-1 mb-2">Pick Slips Allotted</h2>
        <table className="w-full text-xs mb-6 border border-stone-300" data-testid="plan-print-slips-table">
          <thead className="bg-stone-100">
            <tr>
              <th className="text-left p-1.5 border-r border-stone-300 w-8">#</th>
              <th className="text-left p-1.5 border-r border-stone-300">Slip No</th>
              <th className="text-left p-1.5 border-r border-stone-300">Component</th>
              <th className="text-left p-1.5 border-r border-stone-300">SKU</th>
              <th className="text-left p-1.5 border-r border-stone-300">Description</th>
              <th className="text-left p-1.5 border-r border-stone-300">Shade · Size</th>
              <th className="text-right p-1.5 border-r border-stone-300">Bundles</th>
              <th className="text-right p-1.5 border-r border-stone-300">Total Qty</th>
              <th className="text-left p-1.5">Assigned To</th>
            </tr>
          </thead>
          <tbody>
            {data.pick_slips.length === 0 ? (
              <tr><td className="p-3 text-center text-stone-500" colSpan={9}>No pick slips allotted</td></tr>
            ) : data.pick_slips.map((sl, i) => (
              <tr key={sl.slip_no || i} className="border-t border-stone-200">
                <td className="p-1.5 border-r border-stone-200">{i + 1}</td>
                <td className="p-1.5 border-r border-stone-200 font-mono">{sl.slip_no}</td>
                <td className="p-1.5 border-r border-stone-200">{sl.component_name || '—'}</td>
                <td className="p-1.5 border-r border-stone-200 font-mono">{sl.component_barcode || '—'}</td>
                <td className="p-1.5 border-r border-stone-200">{sl.description || '—'}</td>
                <td className="p-1.5 border-r border-stone-200">{[sl.shade, sl.size].filter(Boolean).join(' · ') || '—'}</td>
                <td className="p-1.5 border-r border-stone-200 text-right">{sl.bundle_count}</td>
                <td className="p-1.5 border-r border-stone-200 text-right">{sl.total_qty}</td>
                <td className="p-1.5">{sl.assigned_to || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div className="text-[10px] text-stone-500 border-t border-stone-300 pt-2 flex justify-between">
          <span>Generated: {fmt(new Date().toISOString())}</span>
          <span>WMS · Combo V2 Bulk Plan</span>
        </div>
      </div>
    </div>
  );
}
